import React from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import useSimilarityAlertService from '../../../app/services/hooks/useSimilarityAlertService';

interface MonitoringCardProps {
    brandRegistrationId: number;
    enabled: boolean;
    canManage: boolean;
    pendingAlerts: number;
    onChange: (enabled: boolean) => void;
}

/**
 * Seguimiento de una marca registrada: cada mañana se revisa el Diario Oficial
 * y, si se publica una marca parecida, se crea una alerta y se avisa por correo.
 */
const MonitoringCard: React.FC<MonitoringCardProps> = ({ brandRegistrationId, enabled, canManage, pendingAlerts, onChange }) => {
    const history = useHistory();
    const { toggleMonitoring, fetchingToggleMonitoring } = useSimilarityAlertService();

    const handleToggle = () => {
        toggleMonitoring(brandRegistrationId, !enabled, {
            onSuccess: (response) => {
                toast.success(response.message);
                onChange(!enabled);
            },
            onError: (response) => {
                toast.error(response.message || 'No se pudo cambiar el seguimiento');
            }
        });
    };

    return (
        <div className="card mb-3" data-testid="monitoring-card">
            <div className="card-body d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div className="d-flex align-items-start gap-3">
                    <div
                        className="d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{ width: 48, height: 48, borderRadius: 12, background: enabled ? '#EEF2FB' : '#F1F2F6', color: enabled ? 'var(--text-primary, #001849)' : '#8A93A8' }}
                    >
                        <i className="fas fa-shield-alt" style={{ fontSize: 22 }} />
                    </div>
                    <div>
                        <h5 className="mb-1">
                            Seguimiento en el Diario Oficial{' '}
                            <span
                                data-testid="monitoring-status"
                                style={{
                                    padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, verticalAlign: 'middle',
                                    background: enabled ? '#E7F6EF' : '#EEF0F4', color: enabled ? '#1E7B4F' : '#5B6478'
                                }}
                            >
                                {enabled ? 'Activo' : 'Pausado'}
                            </span>
                        </h5>
                        <p className="mb-0 text-muted">
                            {enabled
                                ? 'Cada mañana revisamos las marcas publicadas en el Diario Oficial. Si aparece una parecida a la tuya, te avisamos por correo y queda una alerta para revisarla.'
                                : 'El seguimiento está pausado: no se revisa el Diario Oficial para esta marca.'}
                        </p>
                    </div>
                </div>
                <div className="d-flex align-items-center gap-3">
                    {canManage && (
                        <div className="form-check form-switch mb-0">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="monitoring-switch"
                                data-testid="monitoring-switch"
                                checked={enabled}
                                disabled={fetchingToggleMonitoring}
                                onChange={handleToggle}
                            />
                            <label className="form-check-label" htmlFor="monitoring-switch">
                                {enabled ? 'Activo' : 'Activar'}
                            </label>
                        </div>
                    )}
                    {canManage && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary text-nowrap"
                            data-testid="monitoring-alerts"
                            onClick={() => history.push(`/intranet/brand-registration/similarity-alerts?brand_registration_id=${brandRegistrationId}`)}
                        >
                            <i className="fas fa-bell me-1" />
                            Ver alertas{pendingAlerts > 0 ? ` (${pendingAlerts})` : ''}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MonitoringCard;
